import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { LegalDoc } from '../data/copy/types';
import { useLanguage } from '../contexts/LanguageContext';
import { CloseIcon, CrownIcon } from './icons/GoldIcons';
const ease = [0.23, 1, 0.32, 1] as const;
type LegalModalProps = {
  doc: LegalDoc | null;
  updated: string;
  onClose: () => void;
};
export function LegalModal({
  doc,
  updated,
  onClose
}: LegalModalProps) {
  const {
    t
  } = useLanguage();
  const reduceMotion = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = doc !== null;
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-235" data-mp="1fpjc0hdjwxo9">
      {doc ? <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} className="fixed inset-0 z-[100] flex items-end justify-center bg-black/85 p-0 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} data-id="element-236" data-mp="1foqe85wklii9">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-title" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.98
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease
      }} onClick={event => event.stopPropagation()} className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl border border-gold-deep/40 bg-ink-900 shadow-[0_30px_80px_rgba(0,0,0,0.75)] sm:max-h-[85vh] sm:rounded-3xl" data-id="element-237" data-mp="s19uvsra8y3g">
            <div className="flex items-start justify-between gap-6 border-b border-gold-deep/25 bg-ink-800 px-6 py-6 sm:px-9" data-id="element-238" data-mp="16x999xt8lcn">
              <div className="min-w-0" data-id="element-239" data-mp="ay1cjce4ihrn">
                <p className="flex items-center gap-2.5 text-[10px] uppercase tracking-[0.28em] text-gold-muted" data-id="element-240" data-mp="az31mkrz54sd">
                  <CrownIcon className="h-4 w-4 shrink-0 text-gold-soft" strokeWidth={1.5} data-id="element-241" data-mp="3jemn2xmeliwv" />
                  {t.a11y.logo}
                </p>
                <h2 id="legal-title" className="gold-shine mt-3 font-display text-2xl font-semibold leading-tight sm:text-3xl" data-id="element-242" data-mp="3td3l2ejrexsf">
                  {doc.title}
                </h2>
                <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-neutral-500" data-id="element-243" data-mp="18mbejrdwt5ir">
                  {t.legal.updatedLabel} {updated}
                </p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label={t.legal.close} className="shrink-0 rounded-full border border-gold-deep/50 p-2.5 text-gold-soft transition-colors duration-200 hover:border-gold-soft hover:text-gold-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-soft" data-id="element-244" data-mp="13pcr1ror3wtj">
                <CloseIcon className="h-5 w-5" data-id="element-245" data-mp="1674wfgjh7sxs" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-8 text-left sm:px-9" data-id="element-246" data-mp="1txisrj93gdy6">
              <p className="text-[15px] leading-relaxed text-neutral-300" data-id="element-247" data-mp="1tzby8v7z0s4l">{doc.intro}</p>

              <div className="mt-8 space-y-8" data-id="element-248" data-mp="1yyg8zqajmxx1">
                {doc.sections.map(section => <section key={section.heading} data-id="element-249" data-mp="1l45o5k1o3nw9">
                    <h3 className="font-display text-lg text-gold-bright sm:text-xl" data-id="element-250" data-mp="1p6aikkzxwojy">
                      {section.heading}
                    </h3>
                    <div className="mt-3 space-y-3" data-id="element-251" data-mp="2cuk03aq4eq5a">
                      {section.body.map(paragraph => <p key={paragraph} className="text-[15px] leading-relaxed text-neutral-400" data-id="element-252" data-mp="2hnv659n3z2w5">
                          {paragraph}
                        </p>)}
                    </div>
                  </section>)}
              </div>
            </div>

            <div className="border-t border-gold-deep/25 bg-ink-800 px-6 py-5 sm:px-9" data-id="element-253" data-mp="k0calzsnmbk4">
              <p className="text-[13px] leading-relaxed text-neutral-500" data-id="element-254" data-mp="k25gvdzcutrl">{t.legal.contactNote}</p>
            </div>
          </motion.div>
        </motion.div> : null}
    </AnimatePresence>;
}